import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { usePermission } from '../../hooks';
import HaventPermission from '../../assets/HaventPermission';
import usePageWidth from '../shared/usePageWidth';
import { getComplexThemePlanCurrent, clearComplexThemePlanCurrent } from '../../actions/discipline';
import styles from './viewStyles.module.css';

const Current = ({ getCurrent, clearCurrent, current, isLoading, errorStatus }) => {

  usePageWidth('extraWide');

  const { readRight } = usePermission('Справочники', 'Комплексный тем.план (КТП) потоков');
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (readRight && id) getCurrent(id);
    return () => clearCurrent();
  }, [id, readRight])

  // возврат к списку с теми же условиями отбора и страницей
  const handleBack = () => {
    const listState = location.state?.flowPlanList;
    if (listState) history.replace({ pathname: '/flow_plan', state: { flowPlanList: listState } });
    else history.goBack();
  }

  if (!readRight) return <HaventPermission />;

  if (errorStatus) {
    const message = errorStatus === 403 ? 'Нет доступа к данному КТП потока'
      : errorStatus === 404 ? 'КТП потока не найден'
        : 'Сервер вернул ошибку';

    return (
      <div className={styles.page}>
        <div className={styles.error}>
          <div className={styles.errorTitle}>Не удалось открыть КТП потока</div>
          {message}
          <div>
            <button className={styles.backBtn} onClick={handleBack}>
              Вернуться к списку
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !current) {
    return (
      <div className={styles.page}>
        <div className={styles.loader}>
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  const main = current.main || {};
  const groups = current.groups || [];
  const studyScheme = current.study_scheme || [];
  const totalHours = studyScheme.reduce((sum, row) => sum + (row.hours || 0), 0);

  const fields = [
    { label: 'Название потока', value: main.name },
    { label: 'Курс', value: main.education_year },
    { label: 'Семестр', value: main.education_term },
    { label: 'Учебный год', value: main.school_year },
    { label: 'Учебный период', value: main.term },
    { label: 'Дисциплина', value: main.course },
    { label: 'Учебный план', value: main.main_curriculum },
    { label: 'Учебная программа', value: main.work_program },
    { label: 'Тематический план', value: main.theme_plan },
  ];

  const renderValue = (value) => (value || value === 0)
    ? value
    : <span className={styles.dash}>—</span>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={handleBack}>
          ← Назад
        </button>
        <h1 className={styles.title}>КТП потоков</h1>
      </div>

      <div className={styles.tabList}>
        <button
          className={tabIndex === 0 ? styles.tabActive : styles.tab}
          onClick={() => setTabIndex(0)}
        >
          Главная
        </button>
        <button
          className={tabIndex === 1 ? styles.tabActive : styles.tab}
          onClick={() => setTabIndex(1)}
        >
          Схема изучения (СЛС)
        </button>
      </div>

      {tabIndex === 0 && (
        <>
          <h2 className={styles.sectionTitle}>Учебные группы</h2>

          <div className={styles.fieldsGrid}>
            {fields.map(({ label, value }) => (
              <div key={label}>
                <span className={styles.fieldLabel}>{label}</span>
                <div className={styles.fieldValue}>{renderValue(value)}</div>
              </div>
            ))}
          </div>

          {groups.length === 0 ? (
            <div className={styles.noData}>Данные отсутствуют</div>
          ) : (
            <>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.colSt}>Группа</th>
                      <th>Учебный план</th>
                      <th>Дисциплина</th>
                      <th>Учебная программа</th>
                      <th>Тематический план</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((row, i) => (
                      <tr key={row.id_ctpsg != null ? `${row.id_ctpsg}-${i}` : i}>
                        <td className={`${styles.colSt} ${styles.mono}`}>{renderValue(row.groupname)}</td>
                        <td className={styles.left}>{renderValue(row.curriculum)}</td>
                        <td className={styles.left}>{renderValue(row.course)}</td>
                        <td className={styles.left}>{renderValue(row.curriculum_course)}</td>
                        <td className={styles.left}>{renderValue(row.themes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className={styles.tableFooter}>Всего групп: <b>{groups.length}</b></div>
            </>
          )}
        </>
      )}

      {tabIndex === 1 && (
        <>
          <h2 className={styles.sectionTitle}>Схема изучения (СЛС)</h2>

          {studyScheme.length === 0 ? (
            <div className={styles.noData}>Данные отсутствуют</div>
          ) : (
            <>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.colSt}>Код</th>
                      <th>Наименование</th>
                      <th className={styles.colSt}>Вид занятия</th>
                      <th className={styles.colSt}>Поток</th>
                      <th className={styles.colHh}>Часы</th>
                      <th className={styles.colSt}>Преподаватель</th>
                      <th className={styles.colSt}>Аудитория</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studyScheme.map((row, i) => (
                      <tr key={row.id_ctpsi != null ? `${row.id_ctpsi}-${i}` : i}>
                        <td className={styles.colSt}>{renderValue(row.o_title_index)}</td>
                        <td className={styles.left}>{renderValue(row.o_title)}</td>
                        <td className={styles.colSt}>{renderValue(row.event_tools)}</td>
                        <td className={`${styles.colSt} ${styles.mono}`}>{renderValue(row.groups)}</td>
                        <td className={styles.colHh}>{renderValue(row.hours)}</td>
                        <td className={styles.colSt}>{renderValue(row.teachers)}</td>
                        <td className={styles.colSt}>{renderValue(row.rooms)}</td>
                      </tr>
                    ))}
                    <tr className={styles.totalRow}>
                      <td></td>
                      <td className={styles.left}>Всего часов</td>
                      <td></td>
                      <td></td>
                      <td className={styles.colHh}>{totalHours}</td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className={styles.tableFooter}>Всего тем: <b>{studyScheme.length}</b></div>
            </>
          )}
        </>
      )}
    </div>
  )
}

const mapStateToProps = ({ complexTopicsDisciplines }) => ({
  current: complexTopicsDisciplines.current,
  isLoading: complexTopicsDisciplines.currentStatus.isLoading,
  errorStatus: complexTopicsDisciplines.currentStatus.errorStatus,
});

const mapDispatchToProps = dispatch => ({
  getCurrent: (id) => dispatch(getComplexThemePlanCurrent(id)),
  clearCurrent: () => dispatch(clearComplexThemePlanCurrent()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Current)
